export type Tab =
  | "home"
  | "requests"
  | "videos"
  | "submit"
  | "inbox"
  | "updates"
  | "script";

/** A key into the `shell` catalogue, translated where it renders. */
export type TabTitleKey = `tabs.${Tab}.title`;
export type NavLabelKey = TabTitleKey | "tabs.home.nav";

/**
 * What each screen is called.
 *
 * The phone header carries the wordmark rather than the screen's name, so
 * these are the titles a screen prints in its own content, and the source the
 * rail's `NAV_LABELS` is built from.
 */
export const TAB_TITLES: Record<Tab, TabTitleKey> = {
  home: "tabs.home.title",
  requests: "tabs.requests.title",
  videos: "tabs.videos.title",
  submit: "tabs.submit.title",
  inbox: "tabs.inbox.title",
  updates: "tabs.updates.title",
  script: "tabs.script.title",
};

/**
 * What navigation calls each destination, which is not always its title.
 *
 * Home's title is the product's name. In a rail the wordmark is already two
 * rows above it, so the item has to say what it is: Home.
 */
export const NAV_LABELS: Record<Tab, NavLabelKey> = {
  ...TAB_TITLES,
  home: "tabs.home.nav",
};

/**
 * Where each tab lives.
 *
 * Here rather than in `PortalShell` because the loading shell needs the same
 * answer: it has to know which rail item to mark active and which screen's
 * skeleton to draw, and a second copy of this map is a second place for a
 * route to be forgotten.
 */
export const TAB_PATH: Record<Tab, string> = {
  home: "/one",
  requests: "/one/requests",
  videos: "/one/videos",
  submit: "/one/submit",
  inbox: "/one/inbox",
  updates: "/one/updates",
  script: "/one/script",
};

/** Longest path wins, so `/one/videos` is not also Home. */
export function tabForPath(pathname: string): Tab {
  return (
    Object.entries(TAB_PATH)
      .filter(([, path]) => path !== "/one" && pathname.startsWith(path))
      .map(([id]) => id as Tab)[0] ?? "home"
  );
}
